import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { X } from 'lucide-react';
import { useUserInfo } from 'pages/UserProvider';
import { getDefaultLandingPage } from 'routes/routes';
import { Button } from '@/components/ui/button';
import { useSpoofedCompanyId } from '@/hooks/useSpoofedCompanyId';

/**
 * Banner shown while an admin is switched into another company.
 *
 * Purely presentational: the spoof token is renewed for as long as the admin's own login
 * lasts by `useSpoofSessionRenewal` in `UserProvider`, so there is no countdown to show
 * and no timer to own here.
 */
const CompanyBanner = (): JSX.Element | null => {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { company, setCompany } = useUserInfo();
  const spoofedCompanyId = useSpoofedCompanyId();

  const handleExit = () => {
    setCompany(null, getDefaultLandingPage());
    navigate(getDefaultLandingPage());
  };

  if (!spoofedCompanyId) {
    return null;
  }

  return (
    <div className="flex items-center justify-between gap-4 bg-yellow px-4 py-2 text-sm text-white">
      <span className="font-medium">
        {t('spoof-banner-viewing-as', 'Viewing as:')} {company?.companyName}
      </span>

      <Button
        variant="ghost"
        size="sm"
        className="h-7 gap-1.5 text-white hover:bg-white/20 hover:text-white"
        onClick={handleExit}
      >
        <X className="size-3.5" />
        {t('spoof-banner-exit', 'Exit')}
      </Button>
    </div>
  );
};

export default CompanyBanner;
